import React, { useState, useRef } from 'react';
import '../styles/home.css';
import Modal from '@cloudscape-design/components/modal';
import Box from '@cloudscape-design/components/box';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Button from '@cloudscape-design/components/button';
import FormField from '@cloudscape-design/components/form-field';
import Spinner from '@cloudscape-design/components/spinner';
import ProgressBar from '@cloudscape-design/components/progress-bar';
import { addUsage } from './add-usage.js';
import { PDFDocument } from 'pdf-lib';
import constants from '../constants.js';
import * as pdfjsLib from 'pdfjs-dist/build/pdf';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.entry';
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

// Width (in CSS px) the page preview is fit to inside the editor. The preview
// is rendered at PREVIEW_SCALE so that previewWidth == PREVIEW_WIDTH.
const PREVIEW_WIDTH = 600;

// Render scale used to rasterize each page when applying redactions. ~2.0
// gives crisp, high quality output. Redaction rects (stored in preview pixels)
// are mapped onto this high-res canvas via the ratio (renderScale/previewScale).
const RENDER_SCALE = 2.0;

// Minimum size (in preview px) for a committed redaction box so accidental
// clicks don't add tiny boxes.
const MIN_BOX_SIZE = 8;

const RedactPdf = ({ isNew }) => {
  const [visible, setVisible] = useState(false);
  const [file, setFile] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressLabel, setProgressLabel] = useState('');

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [pagePreview, setPagePreview] = useState(null);
  const [pageDimensions, setPageDimensions] = useState({ width: 0, height: 0 });
  // Per-page scale used to render the preview. preview px = points * previewScale.
  const [previewScale, setPreviewScale] = useState(1);

  // Committed redaction rectangles in PREVIEW pixel coords: { id, page, x, y, w, h }.
  const [rects, setRects] = useState([]);
  // Live rectangle being drawn (preview px) or null.
  const [drawing, setDrawing] = useState(null);

  const fileInputRef = useRef(null);
  const pdfDocRef = useRef(null);
  const containerRef = useRef(null);

  const resetState = () => {
    setFile(null);
    setErrorMessage('');
    setIsProcessing(false);
    setIsDone(false);
    setProgress(0);
    setProgressLabel('');
    setCurrentPage(1);
    setTotalPages(0);
    setPagePreview(null);
    setPageDimensions({ width: 0, height: 0 });
    setPreviewScale(1);
    setRects([]);
    setDrawing(null);
    pdfDocRef.current = null;
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const toggleModal = () => {
    setVisible(!visible);
    if (!visible) {
      resetState();
    }
  };

  const onDismiss = () => {
    setVisible(false);
    resetState();
  };

  const handleFileChange = async (event) => {
    const selectedFile = event.target.files[0];
    if (!selectedFile || selectedFile.type !== constants.pdfFileType) {
      setFile(null);
      setErrorMessage(constants.erroredFile);
      return;
    }
    setFile(selectedFile);
    setErrorMessage('');
    setRects([]);
    setDrawing(null);
    setIsDone(false);

    try {
      const arrayBuffer = await selectedFile.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      pdfDocRef.current = pdf;
      setTotalPages(pdf.numPages);
      setCurrentPage(1);
      await renderPage(pdf, 1);
    } catch (error) {
      console.error('Failed to load PDF:', error);
      pdfDocRef.current = null;
      if (error.message && (error.message.includes('encrypted') || error.message.includes('password'))) {
        setErrorMessage(constants.encryptedFile);
      } else {
        setErrorMessage('Failed to load the PDF. Please try again.');
      }
    }
  };

  // Render a page to a dataURL preview and record the scale so preview-pixel
  // rects can later be mapped to page points / high-res canvas coords.
  const renderPage = async (pdfDoc, pageNum) => {
    const page = await pdfDoc.getPage(pageNum);
    const baseViewport = page.getViewport({ scale: 1 });
    const newScale = PREVIEW_WIDTH / baseViewport.width;
    setPreviewScale(newScale);

    const scaledViewport = page.getViewport({ scale: newScale });
    setPageDimensions({ width: scaledViewport.width, height: scaledViewport.height });

    let canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    canvas.width = scaledViewport.width;
    canvas.height = scaledViewport.height;
    context.fillStyle = 'white';
    context.fillRect(0, 0, canvas.width, canvas.height);
    await page.render({ canvasContext: context, viewport: scaledViewport }).promise;
    setPagePreview(canvas.toDataURL('image/png'));

    canvas.width = 0;
    canvas.height = 0;
    canvas = null;
  };

  const handlePageChange = async (newPage) => {
    if (newPage < 1 || newPage > totalPages || !pdfDocRef.current) return;
    setDrawing(null);
    setCurrentPage(newPage);
    await renderPage(pdfDocRef.current, newPage);
  };

  // ---- Drawing redaction boxes (coords are in preview pixels) ----

  const getRelativePoint = (e) => {
    const rect = containerRef.current.getBoundingClientRect();
    let x = e.clientX - rect.left;
    let y = e.clientY - rect.top;
    // Clamp inside the preview bounds.
    x = Math.max(0, Math.min(x, pageDimensions.width));
    y = Math.max(0, Math.min(y, pageDimensions.height));
    return { x, y };
  };

  const handleMouseDown = (e) => {
    if (isProcessing || !pagePreview) return;
    e.preventDefault();
    const { x, y } = getRelativePoint(e);
    setDrawing({ startX: x, startY: y, x, y, w: 0, h: 0 });
  };

  const handleMouseMove = (e) => {
    if (!drawing || !containerRef.current) return;
    const { x, y } = getRelativePoint(e);
    const nx = Math.min(drawing.startX, x);
    const ny = Math.min(drawing.startY, y);
    const w = Math.abs(x - drawing.startX);
    const h = Math.abs(y - drawing.startY);
    setDrawing({ ...drawing, x: nx, y: ny, w, h });
  };

  const handleMouseUp = () => {
    if (!drawing) return;
    const { x, y, w, h } = drawing;
    setDrawing(null);
    // Ignore tiny boxes from accidental clicks.
    if (w < MIN_BOX_SIZE || h < MIN_BOX_SIZE) return;
    setRects((prev) => [...prev, { id: Date.now() + Math.random(), page: currentPage, x, y, w, h }]);
  };

  const removeRect = (id) => {
    setRects((prev) => prev.filter((r) => r.id !== id));
  };

  const clearCurrentPage = () => {
    setRects((prev) => prev.filter((r) => r.page !== currentPage));
  };

  const currentPageRects = rects.filter((r) => r.page === currentPage);

  // ---- Apply redactions permanently ----

  const applyRedactions = async () => {
    if (!file || !pdfDocRef.current || rects.length === 0) return;

    setIsProcessing(true);
    setIsDone(false);
    setErrorMessage('');
    setProgress(0);

    try {
      const pdfDoc = pdfDocRef.current;
      const pages = pdfDoc.numPages;
      const outPdf = await PDFDocument.create();

      for (let i = 1; i <= pages; i++) {
        setProgressLabel(`Redacting page ${i} of ${pages}`);

        const page = await pdfDoc.getPage(i);

        // Original page dimensions in points (scale 1) - used to size the output page.
        const baseViewport = page.getViewport({ scale: 1.0 });
        const pageWidthPt = baseViewport.width;
        const pageHeightPt = baseViewport.height;

        // High resolution viewport for crisp rasterization.
        const viewport = page.getViewport({ scale: RENDER_SCALE });

        let canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.width = viewport.width;
        canvas.height = viewport.height;

        // White background so transparent areas become white.
        context.fillStyle = 'white';
        context.fillRect(0, 0, canvas.width, canvas.height);

        // Rasterize the full page content (text, graphics) onto the canvas.
        await page.render({
          canvasContext: context,
          viewport,
          renderInteractiveForms: true,
        }).promise;

        // Paint solid black over each redaction region directly onto the raster.
        // Preview was rendered at previewScale; this canvas is at RENDER_SCALE.
        // Map preview px -> canvas px with ratio = RENDER_SCALE / previewScale.
        const ratio = RENDER_SCALE / previewScale;
        const pageRects = rects.filter((r) => r.page === i);
        context.fillStyle = '#000';
        for (const r of pageRects) {
          context.fillRect(r.x * ratio, r.y * ratio, r.w * ratio, r.h * ratio);
        }

        // Embed the flattened (and redacted) raster into the output PDF.
        const imgData = canvas.toDataURL('image/png');
        const img = await outPdf.embedPng(imgData);

        const newPage = outPdf.addPage([pageWidthPt, pageHeightPt]);
        newPage.drawImage(img, {
          x: 0,
          y: 0,
          width: pageWidthPt,
          height: pageHeightPt,
        });

        // Free the canvas to avoid memory blowup on large PDFs.
        canvas.width = 0;
        canvas.height = 0;
        canvas = null;

        setProgress(Math.round((i / pages) * 100));
      }

      const bytes = await outPdf.save();
      const blob = new Blob([bytes], { type: constants.pdfFileType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `redacted_${file.name}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      addUsage('AWESOME PDF TOOLS RedactPdf', '1.0', 1, window.location.href);

      setProgress(100);
      setIsProcessing(false);
      setIsDone(true);
    } catch (error) {
      console.error('Redaction failed:', error);
      setIsProcessing(false);
      if (error.message && (error.message.includes('encrypted') || error.message.includes('password'))) {
        setErrorMessage(constants.encryptedFile);
      } else {
        setErrorMessage('Redaction failed. Please try again.');
      }
    }
  };

  const redactAnother = () => {
    resetState();
  };

  const totalBoxes = rects.length;

  return (
    <div>
      <div className="service-card" onClick={toggleModal}>
        {isNew && <span className="badge badge-new">NEW</span>}
        <div className="service-card-icon">
          <svg viewBox="0 0 24 24" fill="#FF9900" width="40" height="40">
            <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM9 6c0-1.66 1.34-3 3-3s3 1.34 3 3v2H9V6zm3 11c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z" />
          </svg>
        </div>
        <h3 className="service-card-title">Redact PDF</h3>
        <p className="service-card-desc">
          Permanently remove sensitive information by drawing black boxes that destroy the content underneath
        </p>
        <div className="service-card-action">
          <span>Get Started</span>
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z" />
          </svg>
        </div>
      </div>

      <Modal
        onDismiss={onDismiss}
        visible={visible}
        size="max"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={onDismiss}>
                {constants.cancel}
              </Button>
              {!isDone && (
                <Button
                  variant="primary"
                  onClick={applyRedactions}
                  disabled={isProcessing || totalBoxes === 0}
                >
                  {isProcessing ? <Spinner /> : 'Apply Redactions & Download'}
                </Button>
              )}
            </SpaceBetween>
          </Box>
        }
        header="REDACT PDF — Permanently remove sensitive information"
      >
        <div>
          <div className="bordered-box">
            <div className="container-header">🔒 Redact PDF</div>

            <div
              style={{
                backgroundColor: '#fff7e6',
                border: '1px solid #ffd591',
                borderRadius: '6px',
                padding: '10px 12px',
                marginBottom: '14px',
                fontSize: '13px',
                color: '#874d00',
              }}
            >
              <strong>⚠️ Redaction is permanent.</strong> Each page is rasterized to an image and the black
              boxes are painted onto the pixels, so the covered content is destroyed in the output PDF — there
              is no recoverable text or data underneath.
            </div>

            {!isDone ? (
              <div>
                <FormField
                  label={constants.uploadFile}
                  description={constants.onlyPdfSupported}
                  errorText={errorMessage}
                >
                  <input
                    ref={fileInputRef}
                    id="redact-file-input"
                    type="file"
                    accept=".pdf"
                    onChange={handleFileChange}
                    disabled={isProcessing}
                  />
                </FormField>

                {pagePreview && (
                  <div style={{ marginTop: '16px' }}>
                    {/* Toolbar: page navigation + per-page clear */}
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '12px',
                        padding: '8px',
                        backgroundColor: '#f5f5f5',
                        borderRadius: '6px',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <button
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage <= 1 || isProcessing}
                          style={{
                            padding: '4px 8px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            cursor: currentPage <= 1 ? 'not-allowed' : 'pointer',
                            backgroundColor: '#fff',
                          }}
                        >
                          ◀
                        </button>
                        <span style={{ fontSize: '13px' }}>
                          Page {currentPage} / {totalPages}
                        </span>
                        <button
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage >= totalPages || isProcessing}
                          style={{
                            padding: '4px 8px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer',
                            backgroundColor: '#fff',
                          }}
                        >
                          ▶
                        </button>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span style={{ fontSize: '13px', color: '#666' }}>
                          {currentPageRects.length} box(es) on this page · {totalBoxes} total
                        </span>
                        <button
                          onClick={clearCurrentPage}
                          disabled={currentPageRects.length === 0 || isProcessing}
                          style={{
                            padding: '4px 10px',
                            border: '1px solid #ff4d4f',
                            borderRadius: '4px',
                            cursor: currentPageRects.length === 0 ? 'not-allowed' : 'pointer',
                            backgroundColor: '#fff',
                            color: '#ff4d4f',
                            fontSize: '12px',
                          }}
                        >
                          Clear this page
                        </button>
                      </div>
                    </div>

                    <div style={{ fontSize: '13px', color: '#666', marginBottom: '8px' }}>
                      Drag on the page to draw a black redaction box over anything you want destroyed.
                    </div>

                    <div
                      style={{ overflow: 'auto', maxHeight: '600px', border: '1px solid #ddd', borderRadius: '4px' }}
                    >
                      <div
                        ref={containerRef}
                        onMouseDown={handleMouseDown}
                        onMouseMove={handleMouseMove}
                        onMouseUp={handleMouseUp}
                        onMouseLeave={handleMouseUp}
                        style={{
                          position: 'relative',
                          width: pageDimensions.width,
                          height: pageDimensions.height,
                          margin: '0 auto',
                          backgroundImage: `url(${pagePreview})`,
                          backgroundSize: 'contain',
                          backgroundRepeat: 'no-repeat',
                          cursor: 'crosshair',
                          userSelect: 'none',
                        }}
                      >
                        {/* Committed redaction boxes (solid black, slight opacity to confirm placement) */}
                        {currentPageRects.map((r) => (
                          <div
                            key={r.id}
                            style={{
                              position: 'absolute',
                              left: r.x,
                              top: r.y,
                              width: r.w,
                              height: r.h,
                              backgroundColor: 'rgba(0,0,0,0.9)',
                              boxSizing: 'border-box',
                            }}
                          >
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                removeRect(r.id);
                              }}
                              onMouseDown={(e) => e.stopPropagation()}
                              title="Remove this box"
                              style={{
                                position: 'absolute',
                                top: -10,
                                right: -10,
                                width: '20px',
                                height: '20px',
                                borderRadius: '50%',
                                backgroundColor: '#ff4d4f',
                                color: '#fff',
                                border: 'none',
                                cursor: 'pointer',
                                fontSize: '14px',
                                lineHeight: '1',
                              }}
                            >
                              ×
                            </button>
                          </div>
                        ))}

                        {/* Live box being drawn (semi-transparent black) */}
                        {drawing && (
                          <div
                            style={{
                              position: 'absolute',
                              left: drawing.x,
                              top: drawing.y,
                              width: drawing.w,
                              height: drawing.h,
                              backgroundColor: 'rgba(0,0,0,0.5)',
                              border: '1px dashed #FF9900',
                              boxSizing: 'border-box',
                              pointerEvents: 'none',
                            }}
                          />
                        )}
                      </div>
                    </div>

                    {isProcessing && (
                      <div style={{ marginTop: '15px' }}>
                        <FormField label="Redaction Progress">
                          <ProgressBar value={progress} label={`${progress}%`} description={progressLabel} />
                        </FormField>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div style={{ marginTop: '10px' }}>
                <Box variant="p">
                  ✅ Your PDF has been permanently redacted and downloaded. The covered content has been
                  destroyed in the output file.
                </Box>
                <div style={{ marginTop: '10px' }}>
                  <Button variant="link" onClick={redactAnother}>
                    Redact another
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default RedactPdf;
